import './memory.css';

import cardBack from './assets/images/card-back.png';

/**
 * Osadzenie silnika gry Memory (window.MemoryGame) w konkursie Rexona.
 *
 * Silnik jest gotowym buildem (memory.min.js) wgrywanym przez <script>, nie modułem ES —
 * adres bierzemy z atrybutu data-engine-src sekcji gry (asset() -> manifest + CDN), a stała
 * poniżej jest tylko awaryjnym zapasem, gdyby szablon był nieaktualny.
 *
 * Cała konfiguracja rozgrywki — pula punktów, kara za błąd, tempo wypalania — przychodzi
 * z /start, bo mieszka w adminie (Gra -> Konfiguracje gier). Tutaj zostaje wyłącznie
 * warstwa wizualna i spinanie zdarzeń z resztą strony.
 */
const SCRIPT_URL_FALLBACK = '/build/games/memory/memory.min.js';

function apiBase(uuid) {
    return `/api/application/${uuid}/memory`;
}

let gameInstance = null;
let scriptPromise = null;

function loadScript(src) {
    if (window.MemoryGame) return Promise.resolve();
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
            existing.addEventListener('load', resolve);
            existing.addEventListener('error', reject);
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });

    return scriptPromise;
}

/**
 * Wygląd planszy. Silnik zamienia `colors` i `tokens` na zmienne CSS --mem-*, więc wszystko,
 * czego się tu nie da wyrazić, nadpisuje się w s-game-modal.scss.
 *
 * @param {string|null} cardBack adres rewersu (wspólny dla wszystkich plansz)
 */
function buildAssetsConfig(cardBack) {
    return {
        theme: 'rexona',

        tokens: {
            fontFamily: "'GT Eesti Display', 'Helvetica Neue', 'Segoe UI', sans-serif",
        },

        // HUD rysuje sam silnik: licznik punktów wypala się razem z zegarem, więc musi
        // odświeżać się w tej samej klatce co on. Zegar z setnymi, bo w takiej
        // rozdzielczości liczy się wynik.
        hud: {
            topbar: [{ type: 'score', label: 'PUNKTY' }, { type: 'mistakes', label: 'BŁĘDY' }],
            overlayClose: false,
        },

        texts: {
            gameTitle: 'Rozegraj to po mistrzowsku',
            gameSubtitle: 'Znajdź wszystkie pary!',
            startButton: 'Graj',
            loading: 'Ładowanie...',
            errorPrefix: 'Błąd:',
            close: 'Zamknij',

            roundHeading: 'Znajdź wszystkie pary w jak najkrótszym czasie i zdobądź jak najwięcej punktów.',

            hudScore: 'PUNKTY',
            hudMistakes: 'BŁĘDY',

            resultTitle: 'Twój wynik',
            resultTitleDepleted: 'Punkty się skończyły!',
            resultTitleTimeout: 'Czas minął!',
            resultPairs: 'Znalezione pary',
            resultMistakes: 'Błędy',
            resultTime: 'Czas',
            resultScore: 'Punkty',
            playAgain: 'Zagraj ponownie',
        },

        images: {
            background: null,
            cardBack: cardBack || null,
            betweenIcon: null,
            logo: null,
        },

        sounds: {
            flip: null,
            match: null,
            mismatch: null,
            roundComplete: null,
            gameOver: null,
            countdown: null,
        },

        colors: {
            bgStart: '#0A3C47',
            bgMid: '#0A3C47',
            bgEnd: '#118787',
            btnGradient: 'linear-gradient(270deg, #FC4601 0%, #FE7901 100%)',
            surface: '#FFFFFF',
            onSurface: '#0A3C47',
            text: '#FFFFFF',
            outline: '#118787',
            levelBg: '#0A3C47',
            levelLabelBg: '#FFFFFF',
            timerBg: '#0A3C47',
            timerText: '#FFFFFF',
            timerDanger: '#FC4601',
            closeBg: '#0A3C47',
            closeIcon: '#FFFFFF',
            cardBackColor: '#118787',
            matched: '#118787',
            wrong: '#FC4601',
            scrim: 'rgba(10, 60, 71, 0.75)',
        },
    };
}

/**
 * Uruchamia jedną rozgrywkę w podanym kontenerze.
 *
 * Silnik sam obsługuje /start, /reveal, /heartbeat, /level i /result. Wynik zapisuje
 * serwer w /result i odsyła go z powrotem, więc `onDone(score, reason, result)` dostaje
 * wartość wiążącą, a nie tę policzoną w przeglądarce.
 *
 * @param {HTMLElement} container
 * @param {object}      options
 * @param {string}      options.uuid    uuid zgłoszenia
 * @param {string}      options.kind    'ranked' albo 'trial'
 */
export function startMemory(container, {
    uuid = null,
    kind = 'ranked',
    scriptUrl = null,
    onDone = () => {},
    onError = null,
    onScore = null,
    onStart = null,
} = {}) {
    teardownMemory();
    container.classList.add('mem-game');

    // Rodzaj gry jedzie w ciele POST-a do /start; pozostałe endpointy identyfikuje już
    // token sesji, więc nie muszą o nim wiedzieć.
    const apiConfig = {
        useMock: false,
        startPayload: { kind },
        endpoints: {
            startGame: `${apiBase(uuid)}/start`,
            revealCard: `${apiBase(uuid)}/reveal`,
            submitLevel: `${apiBase(uuid)}/level`,
            heartbeat: `${apiBase(uuid)}/heartbeat`,
            submitResult: `${apiBase(uuid)}/result`,
        },
    };

    return loadScript(scriptUrl || SCRIPT_URL_FALLBACK)
        .then(() => {
            if (!window.MemoryGame) {
                throw new Error('MemoryGame: skrypt wczytany, ale globalna nie istnieje');
            }

            gameInstance = window.MemoryGame.init(container.id, {
                // Rewers domyślny jedzie w paczce frontu; jeśli w adminie wgrano własny,
                // /start dosyła go w `assetsConfig` i silnik nadpisze nim ten poniżej.
                assetsConfig: buildAssetsConfig(cardBack),
                gameConfig: {
                    // Tokeny z /start; obrazek i grupa pary dociągane przy pierwszym odkryciu.
                    cardSource: 'reveal',
                    autoStart: true,

                    // Układ dyktuje serwer (plansza dnia), więc nic tu nie tasujemy ani
                    // nie liczymy kolumn samodzielnie.
                    shuffleSeed: null,

                    // Zegar rusza dopiero przy pierwszym kliknięciu w kartę — tak mówi regulamin.
                    timerStart: 'first-flip',
                    timerFormat: 'mm:ss.cc',

                    // Wynik liczy serwer swoim zegarem, więc licznik na planszy musi znać
                    // opóźnienie sieci — inaczej na wolnym łączu pokazywałby co innego niż
                    // ekran wyniku. Korekta dotyczy WYŁĄCZNIE tego, co widać; do serwera
                    // silnik dalej raportuje surowy czas.
                    syncClockToServer: true,

                    // Limit czasu wynika z punktacji: pula wypala się sama. Osobnego odliczania
                    // nie ma, żeby nie istniały dwa źródła prawdy o końcu gry.
                    timeLimitSeconds: null,

                    resolveDelayMs: 500,
                    mismatchDelayMs: 500,
                    roundCompleteDelayMs: 600,
                    showBetweenRounds: false,
                    previewSeconds: 0,

                    // Wartości domyślne; /start nadpisze je konfiguracją z panelu.
                    scoring: {
                        mode: 'decay',
                        startPoints: 10000,
                        decayPerSecond: 100,
                        mistakePenalty: 200,
                        endAtZero: true,
                    },

                    // Nadpisywane przez /start. Heartbeaty są też próbkami do wyznaczenia
                    // opóźnienia sieci (patrz MemoryScoringService), więc im gęściej, tym
                    // dokładniejszy serwerowy pomiar czasu.
                    heartbeatInterval: 5000,
                },
                apiConfig,
                callbacks: {
                    'game:start': () => {
                        // /start zdążył domieszać swoją konfigurację, więc dopiero teraz
                        // znamy realną pulę punktów i tempo jej wypalania.
                        onStart?.(gameInstance?.gameConfig ?? null);
                    },

                    // Wynik wypala się z zegarem, więc zmienia się sam z siebie — stąd
                    // `score:tick` obok zdarzeń par. Przydatne, gdy strona chce pokazać
                    // wynik poza HUD-em silnika.
                    'score:tick': (e) => onScore?.(e?.score ?? 0),
                    'pair:matched': (e) => onScore?.(e?.score ?? 0),
                    'pair:missed': (e) => onScore?.(e?.score ?? 0),

                    onComplete: (result) => {
                        // /result jest wiążący, a silnik nadpisuje nim swój payload,
                        // więc result.score to liczba z serwera.
                        const score = result?.score ?? 0;
                        const reason = result?.reason ?? null;
                        teardownMemory();
                        onDone(score, reason, result ?? null);
                    },

                    onError: (error) => {
                        console.error('MemoryGame error:', error);
                        teardownMemory();
                        // Nieudany /start (plansza nieprzygotowana, gra już rozegrana, konkurs
                        // nieaktywny) przychodzi tu jako ApiError z { status, data } — oddajemy
                        // go wyżej, żeby dało się pokazać sensowny ekran.
                        if (onError) {
                            onError(error);
                        } else {
                            onDone(0, 'error', null);
                        }
                    },
                },
            });
        })
        .catch((e) => {
            console.error('MemoryGame: nie udało się wczytać silnika', e);
            if (onError) {
                onError(e);
            } else {
                onDone(0, 'error', null);
            }
        });
}

export function teardownMemory() {
    if (gameInstance) {
        try {
            gameInstance.destroy();
        } catch { /* ignore */ }
        gameInstance = null;
    }
}

export function pauseMemory() {
    try {
        gameInstance?.pause?.();
    } catch { /* ignore */ }
}

export function resumeMemory() {
    try {
        gameInstance?.resume?.();
    } catch { /* ignore */ }
}
